import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Play, Loader2, CheckCircle2, XCircle, Code2 } from 'lucide-react';
import { toast } from 'sonner';

interface NodeEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  nodeType: string;
  nodeData: any;
  onSave: (updatedData: any) => void;
}

export default function NodeEditDialog({
  isOpen,
  onClose,
  nodeType,
  nodeData,
  onSave,
}: NodeEditDialogProps) {
  const [formData, setFormData] = useState<any>({});

  useEffect(() => {
    setFormData(nodeData || {});
  }, [nodeData]);

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  // Check if this is a vision API step
  const isVisionStep = formData.input?.messages && Array.isArray(formData.input.messages);
  
  // Extract vision prompt if available
  const getVisionPrompt = () => {
    if (!isVisionStep) return '';
    const content = formData.input.messages[0]?.content;
    if (!Array.isArray(content)) return '';
    const textContent = content.find((c: any) => c.type === 'text');
    return textContent?.text || '';
  };

  const renderLLMStepFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="stepName">Step Name</Label>
        <Input
          id="stepName"
          value={formData.stepName || ''}
          onChange={(e) => setFormData({ ...formData, stepName: e.target.value })}
          placeholder="e.g., analyze_data"
        />
      </div>

      {isVisionStep && (
        <div className="bg-purple-50 border border-purple-200 rounded-md p-3 mb-3">
          <div className="flex items-center gap-2 text-purple-700 font-semibold text-sm">
            <span>🖼️</span>
            <span>Vision API Step (GPT-4o)</span>
          </div>
          <p className="text-xs text-purple-600 mt-1">
            This step analyzes images using GPT-4o vision with base64-encoded images from previous workflow steps.
          </p>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="prompt">{isVisionStep ? 'Vision Analysis Prompt' : 'Prompt'}</Label>
          <div className="flex items-center gap-1">
            <Badge variant="outline" className="text-[10px] h-5 px-1.5 font-mono text-blue-600 border-blue-200">
              {'{{input.field}}'}
            </Badge>
            <Badge variant="outline" className="text-[10px] h-5 px-1.5 font-mono text-purple-600 border-purple-200">
              {'{{steps.name.result}}'}
            </Badge>
          </div>
        </div>
        <Textarea
          id="prompt"
          value={isVisionStep ? getVisionPrompt() : (formData.prompt || '')}
          onChange={(e) => {
            if (isVisionStep) {
              // Update vision prompt in the messages structure
              const newMessages = [...formData.input.messages];
              const content = newMessages[0].content.map((c: any) => 
                c.type === 'text' ? { ...c, text: e.target.value } : c
              );
              newMessages[0] = { ...newMessages[0], content };
              setFormData({ 
                ...formData, 
                input: { ...formData.input, messages: newMessages }
              });
            } else {
              setFormData({ ...formData, prompt: e.target.value });
            }
          }}
          placeholder={isVisionStep ? "Instructions for analyzing the image..." : "Enter your prompt here...\n\nUse {{input.field}} to reference input data\nUse {{steps.step_name.result}} to reference previous step outputs"}
          rows={12}
          className="font-mono text-sm"
        />
        <p className="text-[10px] text-gray-500">
          Template variables: <code className="text-blue-600">{'{{input}}'}</code>, <code className="text-blue-600">{'{{input.field}}'}</code>, <code className="text-purple-600">{'{{steps.step_name.field}}'}</code>, <code className="text-purple-600">{'{{context.step_N.field}}'}</code>
        </p>
      </div>

      {isVisionStep && formData.input?.system && (
        <div className="space-y-2">
          <Label htmlFor="system">System Prompt</Label>
          <Textarea
            id="system"
            value={formData.input.system || ''}
            onChange={(e) => setFormData({ 
              ...formData, 
              input: { ...formData.input, system: e.target.value }
            })}
            placeholder="System instructions for the vision AI..."
            rows={3}
            className="font-mono text-sm"
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="provider">Provider</Label>
          <Select
            value={formData.provider || 'anthropic'}
            onValueChange={(value) => setFormData({ ...formData, provider: value })}
          >
            <SelectTrigger id="provider">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="openai">OpenAI</SelectItem>
              <SelectItem value="anthropic">Anthropic</SelectItem>
              <SelectItem value="google">Google</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="model">Model</Label>
          <Select
            value={formData.model || 'claude-haiku-4-5-20251001'}
            onValueChange={(value) => setFormData({ ...formData, model: value })}
          >
            <SelectTrigger id="model">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {formData.provider === 'openai' && (
                <>
                  <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                  <SelectItem value="gpt-4-turbo">GPT-4 Turbo</SelectItem>
                </>
              )}
              {formData.provider === 'anthropic' && (
                <>
                  <SelectItem value="claude-sonnet-4-6">Claude Sonnet 4.6</SelectItem>
                  <SelectItem value="claude-haiku-4-5-20251001">Claude Haiku 4.5</SelectItem>
                  <SelectItem value="claude-sonnet-4-6">Claude Sonnet 4</SelectItem>
                  <SelectItem value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet</SelectItem>
                  <SelectItem value="claude-3-opus-20240229">Claude 3 Opus</SelectItem>
                </>
              )}
              {formData.provider === 'google' && (
                <>
                  <SelectItem value="gemini-2.0-flash-exp">Gemini 2.0 Flash</SelectItem>
                  <SelectItem value="gemini-1.5-pro">Gemini 1.5 Pro</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>
    </>
  );

  const renderAgentCallFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="agentId">Agent ID</Label>
        <Input
          id="agentId"
          value={formData.agentId || ''}
          onChange={(e) => setFormData({ ...formData, agentId: e.target.value })}
          placeholder="e.g., contextualizer-agent"
          disabled
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="agentName">Agent Name</Label>
        <Input
          id="agentName"
          value={formData.agentName || ''}
          onChange={(e) => setFormData({ ...formData, agentName: e.target.value })}
          placeholder="e.g., Contextualizer Agent"
          disabled
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="agentDescription">Description</Label>
        <Textarea
          id="agentDescription"
          value={formData.agentDescription || ''}
          onChange={(e) => setFormData({ ...formData, agentDescription: e.target.value })}
          placeholder="Agent description..."
          rows={3}
          disabled
        />
      </div>
    </>
  );

  const renderToolCallFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="toolName">Tool Name</Label>
        <Input
          id="toolName"
          value={formData.toolName || ''}
          onChange={(e) => setFormData({ ...formData, toolName: e.target.value })}
          placeholder="e.g., mongodb_query"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="toolDescription">Description</Label>
        <Textarea
          id="toolDescription"
          value={formData.toolDescription || ''}
          onChange={(e) => setFormData({ ...formData, toolDescription: e.target.value })}
          placeholder="Tool description..."
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="mcpServer">MCP Server</Label>
        <Input
          id="mcpServer"
          value={formData.mcpServer || ''}
          onChange={(e) => setFormData({ ...formData, mcpServer: e.target.value })}
          placeholder="e.g., mongodb"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="parameters">Parameters (JSON)</Label>
        <Textarea
          id="parameters"
          value={JSON.stringify(formData.parameters || {}, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              setFormData({ ...formData, parameters: parsed });
            } catch (error) {
              // Invalid JSON, don't update
            }
          }}
          placeholder='{"key": "value"}'
          rows={4}
          className="font-mono text-xs"
        />
      </div>
    </>
  );

  const renderHttpStepFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="stepName">Step Name</Label>
        <Input
          id="stepName"
          value={formData.stepName || formData.name || ''}
          onChange={(e) => setFormData({ ...formData, stepName: e.target.value })}
          placeholder="e.g., fetch_data"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="method">Method</Label>
          <Select value={formData.method || 'POST'} onValueChange={(value) => setFormData({ ...formData, method: value })}>
            <SelectTrigger id="method">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="GET">GET</SelectItem>
              <SelectItem value="POST">POST</SelectItem>
              <SelectItem value="PUT">PUT</SelectItem>
              <SelectItem value="PATCH">PATCH</SelectItem>
              <SelectItem value="DELETE">DELETE</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="timeout">Timeout (ms)</Label>
          <Input
            id="timeout"
            type="number"
            value={formData.timeout || 30000}
            onChange={(e) => setFormData({ ...formData, timeout: Number(e.target.value) || 30000 })}
            min={1000}
            step={1000}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="url">URL</Label>
        <Input
          id="url"
          value={formData.url || ''}
          onChange={(e) => setFormData({ ...formData, url: e.target.value })}
          placeholder="https://service/api/{{input.id}}"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description || ''}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
          placeholder="What this HTTP step does..."
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="condition">Condition (optional)</Label>
        <Input
          id="condition"
          value={formData.condition || ''}
          onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
          placeholder="{{context.step_1.ok}} == true"
        />
      </div>
    </>
  );

  const renderValidateFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="stepName">Step Name</Label>
        <Input
          id="stepName"
          value={formData.stepName || formData.name || ''}
          onChange={(e) => setFormData({ ...formData, stepName: e.target.value })}
          placeholder="e.g., validate_input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="on_error">On Error</Label>
        <Select
          value={formData.on_error || 'fail'}
          onValueChange={(value) => setFormData({ ...formData, on_error: value })}
        >
          <SelectTrigger id="on_error">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fail">fail</SelectItem>
            <SelectItem value="log">log</SelectItem>
            <SelectItem value="sanitize">sanitize</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="schema">Schema (JSON)</Label>
        <Textarea
          id="schema"
          value={JSON.stringify(formData.schema || {}, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              setFormData({ ...formData, schema: parsed });
            } catch {
              // ignore invalid JSON
            }
          }}
          placeholder='{"type":"object","required":["id"],"properties":{"id":{"type":"string"}}}'
          rows={8}
          className="font-mono text-xs"
        />
      </div>
    </>
  );

  const renderNotifyFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="stepName">Step Name</Label>
        <Input
          id="stepName"
          value={formData.stepName || formData.name || ''}
          onChange={(e) => setFormData({ ...formData, stepName: e.target.value })}
          placeholder="e.g., notify_webhook"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="channel">Channel</Label>
        <Select
          value={formData.channel || 'webhook'}
          onValueChange={(value) => setFormData({ ...formData, channel: value })}
        >
          <SelectTrigger id="channel">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="webhook">webhook</SelectItem>
            <SelectItem value="slack">slack</SelectItem>
            <SelectItem value="email">email</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="url">Destination URL</Label>
        <Input
          id="url"
          value={formData.url || ''}
          onChange={(e) => setFormData({ ...formData, url: e.target.value })}
          placeholder="https://hooks.example.com/..."
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="payload">Payload (JSON)</Label>
        <Textarea
          id="payload"
          value={JSON.stringify(formData.payload || {}, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              setFormData({ ...formData, payload: parsed });
            } catch {
              // ignore invalid JSON
            }
          }}
          placeholder='{"message":"done","data":"{{input}}"}'
          rows={6}
          className="font-mono text-xs"
        />
      </div>
    </>
  );

  const renderTransformFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="stepName">Step Name</Label>
        <Input
          id="stepName"
          value={formData.stepName || formData.name || ''}
          onChange={(e) => setFormData({ ...formData, stepName: e.target.value })}
          placeholder="e.g., transform_payload"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="transform">Transform (JSON)</Label>
        <Textarea
          id="transform"
          value={JSON.stringify(formData.transform || {}, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              setFormData({ ...formData, transform: parsed });
            } catch {
              // ignore invalid JSON
            }
          }}
          placeholder='{"normalized":{"id":"{{input.id}}"}}'
          rows={8}
          className="font-mono text-xs"
        />
      </div>
    </>
  );

  const renderInputOutputFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="label">Label</Label>
        <Input
          id="label"
          value={formData.label || ''}
          onChange={(e) => setFormData({ ...formData, label: e.target.value })}
          placeholder="e.g., Workflow Input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="type">Type</Label>
        <Select
          value={formData.type || 'input'}
          onValueChange={(value) => setFormData({ ...formData, type: value })}
        >
          <SelectTrigger id="type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="input">Input</SelectItem>
            <SelectItem value="output">Output</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="schema">Schema (JSON)</Label>
        <Textarea
          id="schema"
          value={JSON.stringify(formData.schema || {}, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              setFormData({ ...formData, schema: parsed });
            } catch (error) {
              // Invalid JSON, don't update
            }
          }}
          placeholder='{"field": "type"}'
          rows={4}
          className="font-mono text-xs"
        />
      </div>
    </>
  );

  const renderStartFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="label">Label</Label>
        <Input
          id="label"
          value={formData.label || ''}
          onChange={(e) => setFormData({ ...formData, label: e.target.value })}
          placeholder="e.g., Create Template with AI"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description || ''}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Describe the workflow entry point..."
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="trigger">Trigger Type</Label>
        <Select
          value={formData.trigger || 'Manual'}
          onValueChange={(value) => setFormData({ ...formData, trigger: value })}
        >
          <SelectTrigger id="trigger">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Manual">Manual</SelectItem>
            <SelectItem value="API Call">API Call</SelectItem>
            <SelectItem value="Schedule">Schedule</SelectItem>
            <SelectItem value="Event">Event</SelectItem>
            <SelectItem value="Webhook">Webhook</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </>
  );

  const renderEndFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="label">Label</Label>
        <Input
          id="label"
          value={formData.label || ''}
          onChange={(e) => setFormData({ ...formData, label: e.target.value })}
          placeholder="e.g., Template Created"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description || ''}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Describe the workflow outcome..."
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="status">Status</Label>
        <Select
          value={formData.status || 'success'}
          onValueChange={(value) => setFormData({ ...formData, status: value })}
        >
          <SelectTrigger id="status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="success">Success</SelectItem>
            <SelectItem value="approval">Pending Approval</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="error">Error</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Edit {nodeType === 'llmStep' ? 'LLM Step'
              : nodeType === 'agentCall' ? 'Agent Call'
              : nodeType === 'toolCall' ? 'MCP Tool'
              : nodeType === 'httpStep' ? 'HTTP Step'
              : nodeType === 'validateStep' ? 'Validate Step'
              : nodeType === 'notifyStep' ? 'Notify Step'
              : nodeType === 'transformStep' ? 'Transform Step'
              : nodeType === 'start' ? 'Start Node'
              : nodeType === 'end' ? 'End Node'
              : 'Input/Output'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {nodeType === 'llmStep' && renderLLMStepFields()}
          {nodeType === 'agentCall' && renderAgentCallFields()}
          {nodeType === 'toolCall' && renderToolCallFields()}
          {nodeType === 'httpStep' && renderHttpStepFields()}
          {nodeType === 'validateStep' && renderValidateFields()}
          {nodeType === 'notifyStep' && renderNotifyFields()}
          {nodeType === 'transformStep' && renderTransformFields()}
          {nodeType === 'inputOutput' && renderInputOutputFields()}
          {nodeType === 'start' && renderStartFields()}
          {nodeType === 'end' && renderEndFields()}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

